import React, { useState } from "react";
import { FiX, FiTag, FiPlus, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { setClassLevelDefaultAddOns, extractApiErrorMessage } from "../../utils/classGroupApi";

const SetDefaultAddOnsModal = ({
  isOpen,
  onClose,
  schoolId,
  classGroupId,
  classGroupName = "Class Group",
  currentDefaultAddOns = [], // Array<{ add_on_name: string, add_on_price: string }>
  onSuccess,
}) => {
  const [addOns, setAddOns] = useState(
    currentDefaultAddOns && currentDefaultAddOns.length > 0
      ? currentDefaultAddOns.map((a) => ({
          add_on_name: a.add_on_name || "",
          add_on_price: a.add_on_price || "",
        }))
      : [{ add_on_name: "", add_on_price: "" }],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleAddRow = () => {
    setAddOns((prev) => [...prev, { add_on_name: "", add_on_price: "" }]);
  };

  const handleRemoveRow = (index) => {
    setAddOns((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setAddOns((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanAddOns = addOns.filter((a) => a.add_on_name.trim() && a.add_on_price);

    if (cleanAddOns.length === 0) {
      setError("Please add at least one valid add-on with name and price");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await setClassLevelDefaultAddOns(schoolId, classGroupId, cleanAddOns);

      toast.success(`Class-level default add-ons saved and applied to member arms`);

      if (onSuccess) {
        onSuccess(res);
      }
      onClose();
    } catch (err) {
      console.error("Error setting default add-ons:", err);
      setError(extractApiErrorMessage(err, "Failed to set class-level default add-ons"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <FiTag className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Set default extra fees</h3>
              <p className="text-xs text-slate-500">Group: {classGroupName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-700">
                Extra fees for every class in this group
              </label>
              <button
                type="button"
                onClick={handleAddRow}
                className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
              >
                <FiPlus className="w-3.5 h-3.5" />
                Add another fee
              </button>
            </div>

            <div className="space-y-2.5">
              {addOns.map((row, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={row.add_on_name}
                    onChange={(e) => handleRowChange(index, "add_on_name", e.target.value)}
                    placeholder="Fee Name (e.g. Uniform, Lab)"
                    className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  />
                  <div className="w-28 flex-shrink-0">
                    <input
                      type="number"
                      value={row.add_on_price}
                      onChange={(e) => handleRowChange(index, "add_on_price", e.target.value)}
                      placeholder="Price (₦)"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                  {addOns.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(index)}
                      className="p-2 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-slate-100 flex-shrink-0"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Good to know</span>
            <ul className="list-disc list-inside space-y-0.5 text-slate-600">
              <li>Each fee is added to every class in the group that doesn't already have a fee with the same name.</li>
              <li>If a class already has that fee, its existing price is kept.</li>
            </ul>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            Save Default Fees
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetDefaultAddOnsModal;
